import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'i18next'
import { Tooltip } from '@mui/material'
import {
  DarkMode as DarkModeIcon, LightMode as LightModeIcon, PhotoCameraOutlined, PersonOutline, KeyOutlined,
  ShieldOutlined, TuneOutlined, WarningAmberRounded, SupportAgentOutlined, VerifiedUserOutlined, MailOutline, PhoneIphoneOutlined, LockOutlined,
} from '@mui/icons-material'
import { useDispatch, useSelector } from '../store'
import { deleteUser, putData } from '../store/settings/actionCreator'
import { showSnackbar } from '../store/common/actionCreator'
import { api } from '../function/axios'
import { preventCyrillicOnInput } from '../function/validation'
import { PrivatePage } from '../component/PagePrivate'
import { ViewToggle } from '../component/ViewToggle'
import { NotificationV2 } from '../container/common/NotificationV2'
import { ProfileV2 } from '../container/common/ProfileV2'
import { ConfirmV2 } from '../component/ConfirmV2'
import { TwoFactorV2 } from '../component/settings-v2/TwoFactorV2'
import { PopupImageUpload } from '../container/settings/PopupImageUpload'
import { ChangeEmailV2, ChangePasswordV2, ChangePhoneV2 } from '../component/settings-v2/ChangeDialogsV2'
import { Role } from '../enum/role'
import { useAdminTheme } from '../hook/useAdminTheme'
import { useAdminView } from '../hook/useAdminView'
import l from './SubscribersPageV2.module.css'
import s from '../component/settings-v2/SettingsV2.module.css'

/*
 * The signed-in admin's own settings in the v2 admin design. The classic
 * page (SettingsPage.tsx) is untouched, and so are its flows: the name saves
 * field by field (PUT settings data, Latin letters, up to 50); email,
 * password, phone and two-step verification open v2 dialogs with the classic
 * flows (ChangeDialogsV2, TwoFactorV2: their checks, codes and re-auth stay
 * exactly as they are); the photo opens the classic dialog. Email cannot change while 2FA is on, and deleting
 * asks for a 2FA code when it is on — as there.
 *
 * New on top: the admin's role and department at a glance, and the look of
 * the panel (theme, classic / new view) in one place.
 */

function Section({ icon, title, sub, children, danger }: { icon: React.ReactNode; title: string; sub?: string; children: React.ReactNode; danger?: boolean }) {
  return (
    <section className={`${s.section} ${danger ? s.sectionDanger : ''}`}>
      <div className={s.sectionHead}>
        <span className={s.sectionIcon}>{icon}</span>
        <div><b>{title}</b>{sub && <span>{sub}</span>}</div>
      </div>
      <div className={s.sectionBody}>{children}</div>
    </section>
  )
}

function NameField({ label, value, field, placeholder }: { label: string; value: string | null; field: 'firstname' | 'lastname'; placeholder: string }) {
  const dispatch = useDispatch()
  const [draft, setDraft] = React.useState(value || '')
  React.useEffect(() => { setDraft(value || '') }, [value])
  const changed = draft !== (value || '')
  return (
    <label className={s.field}>
      <span className={s.label}>{label}</span>
      <span className={s.inline}>
        <input
          className={s.input}
          value={draft}
          maxLength={50}
          placeholder={placeholder}
          onChange={(e) => preventCyrillicOnInput(e, setDraft)}
          onKeyDown={(e) => { if (e.key === 'Enter' && changed) { e.preventDefault(); dispatch(putData(field, draft)) } }}
        />
        {changed && (
          <>
            <button type="button" className={s.primarySm} onClick={() => dispatch(putData(field, draft))}>Save</button>
            <button type="button" className={s.ghostSm} onClick={() => setDraft(value || '')}>Undo</button>
          </>
        )}
      </span>
    </label>
  )
}

function Row({ icon, label, value, action, onAction, note }: {
  icon: React.ReactNode; label: string; value: React.ReactNode; action: string; onAction: () => void; note?: string
}) {
  return (
    <div className={s.row}>
      <span className={s.rowIcon}>{icon}</span>
      <div className={s.rowText}>
        <small>{label}</small>
        <span>{value}</span>
        {note && <em>{note}</em>}
      </div>
      <button type="button" className={s.ghost} onClick={onAction}>{action}</button>
    </div>
  )
}

export function SettingsPageV2() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { theme, toggleTheme } = useAdminTheme()
  const { view, setView } = useAdminView()
  const user = useSelector((state) => state.settings)

  const [uploadOpen, setUploadOpen] = React.useState(false)
  const [emailOpen, setEmailOpen] = React.useState(false)
  const [passwordOpen, setPasswordOpen] = React.useState(false)
  const [phoneOpen, setPhoneOpen] = React.useState(false)
  const [qrOpen, setQrOpen] = React.useState(false)
  const [codeFor, setCodeFor] = React.useState<'toggle2FA' | 'deleteUser' | null>(null)
  // "Support notifications" — kept on the admin's account (GET/PUT /notifications/support-settings); null while loading
  const [supportOn, setSupportOn] = React.useState<boolean | null>(null)
  React.useEffect(() => {
    api({ method: 'GET', url: '/notifications/support-settings' })
      .then(({ data }) => setSupportOn(!!data?.enabled))
      .catch(() => setSupportOn(false))
  }, [])
  async function toggleSupport() {
    if (supportOn === null) return
    const next = !supportOn
    setSupportOn(next)
    try {
      await api({ method: 'PUT', url: '/notifications/support-settings', body: { enabled: next } })
      dispatch(showSnackbar('success', next ? 'Support notifications are on' : 'Support notifications are off'))
    } catch {
      setSupportOn(!next)
    }
  }
  const [sureDelete, setSureDelete] = React.useState(false)

  const name = [user.firstname, user.lastname].filter(Boolean).join(' ') || user.email || 'Your profile'
  const initials = `${(user.firstname || user.email || '?')[0]}${(user.lastname || '')[0] || ''}`.toUpperCase()
  const department = (user.department as unknown as { name?: string } | null)?.name
  const roleLabel = user.role === Role.SUPER_ADMIN ? 'Super-admin' : user.role === Role.ADMIN ? 'Admin' : user.role || '—'

  function changeEmail() {
    if (user.is2FAEnabled) dispatch(showSnackbar('error', t('settings.turnOff2FaBeforeChangeEmail')))
    else setEmailOpen(true)
  }
  function toggle2FA() {
    if (user.is2FAEnabled) setCodeFor('toggle2FA')
    else setQrOpen(true)
  }
  function remove() {
    if (user.is2FAEnabled) setCodeFor('deleteUser')
    else setSureDelete(true)
  }

  const header = (
    <div className={`${l.theme} ${l.header}`} data-theme={theme}>
      <div className={l.crumb}><small>Account</small><b>Settings</b></div>
      <div className={l.spacer} />
      <ViewToggle dark={theme === 'dark'} />
      <Tooltip title={theme === 'dark' ? 'Light theme' : 'Dark theme'}>
        <button className={l.iconBtn} onClick={toggleTheme} aria-label="Toggle theme">
          {theme === 'dark' ? <LightModeIcon sx={{ fontSize: 17 }} /> : <DarkModeIcon sx={{ fontSize: 17 }} />}
        </button>
      </Tooltip>
      <NotificationV2 />
      <ProfileV2 />
    </div>
  )

  return (
    <PrivatePage header={header}>
      <div className={`${l.theme} ${l.page}`} data-theme={theme}>
        <div className={s.wrap}>
          <div className={s.hero}>
            <button type="button" className={s.avatarBtn} onClick={() => setUploadOpen(true)} aria-label="Change the photo" title="Change the photo">
              {user.avatar ? <img src={user.avatar} alt="" /> : <span>{initials}</span>}
              <i><PhotoCameraOutlined sx={{ fontSize: 15 }} /></i>
            </button>
            <div className={s.heroText}>
              <b>{name}</b>
              <span>{user.email}</span>
              <div className={s.badges}>
                <span className={`${s.badge} ${user.role === Role.SUPER_ADMIN ? s.badgeSuper : ''}`}>
                  {user.role === Role.SUPER_ADMIN && <VerifiedUserOutlined sx={{ fontSize: 13 }} />}{roleLabel}
                </span>
                {department && <span className={s.badge}>{department}</span>}
                <span className={`${s.badge} ${user.is2FAEnabled ? s.badgeOk : s.badgeWarn}`}>{user.is2FAEnabled ? '2FA on' : '2FA off'}</span>
              </div>
            </div>
          </div>

          <div className={s.grid}>
            <Section icon={<PersonOutline sx={{ fontSize: 18 }} />} title="Personal information" sub="Your name in the panel and in admin records">
              <div className={s.grid2}>
                <NameField label="First name" value={user.firstname} field="firstname" placeholder="Michael" />
                <NameField label="Last name" value={user.lastname} field="lastname" placeholder="Jones" />
              </div>
              <span className={s.hint}>Latin letters only.</span>
            </Section>

            <Section icon={<KeyOutlined sx={{ fontSize: 18 }} />} title="Sign-in" sub="How you get into the admin panel">
              <Row
                icon={<MailOutline sx={{ fontSize: 17 }} />}
                label="Email"
                value={user.email || '—'}
                action="Change"
                onAction={changeEmail}
                note={user.is2FAEnabled ? 'Turn off two-step verification to change it' : undefined}
              />
              <Row icon={<LockOutlined sx={{ fontSize: 17 }} />} label="Password" value="••••••••••" action="Change" onAction={() => setPasswordOpen(true)} />
              <Row icon={<PhoneIphoneOutlined sx={{ fontSize: 17 }} />} label="Phone" value={user.phone || <span className={l.muted}>Not set</span>} action={user.phone ? 'Change' : 'Add'} onAction={() => setPhoneOpen(true)} />
            </Section>

            <Section icon={<ShieldOutlined sx={{ fontSize: 18 }} />} title="Two-step verification" sub="A code from an authenticator app on every sign-in">
              <div className={`${s.twoFA} ${user.is2FAEnabled ? s.twoFAOn : ''}`}>
                <div className={s.rowText}>
                  <small>Status</small>
                  <span>{user.is2FAEnabled ? 'On — your account asks for a code' : 'Off — a password is enough to sign in'}</span>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!user.is2FAEnabled}
                  aria-label="Two-step verification"
                  className={`${s.switch} ${user.is2FAEnabled ? s.switchOn : ''}`}
                  onClick={toggle2FA}
                >
                  <span />
                </button>
              </div>
              {!user.is2FAEnabled && <div className={s.warn}><WarningAmberRounded sx={{ fontSize: 17 }} />Recommended for every admin account.</div>}
            </Section>

            <Section icon={<SupportAgentOutlined sx={{ fontSize: 18 }} />} title="Support notifications" sub="New tickets and customers’ replies — in the bell, the menu and a pop-up">
              <div className={`${s.twoFA} ${supportOn ? s.twoFAOn : ''}`}>
                <div className={s.rowText}>
                  <small>Status</small>
                  <span>
                    {supportOn === null ? 'Loading…' : supportOn
                      ? 'On — you hear of new tickets (and replies in tickets assigned to you, or to no one)'
                      : 'Off — nothing is sent to you about tickets'}
                  </span>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!supportOn}
                  aria-label="Support notifications"
                  className={`${s.switch} ${supportOn ? s.switchOn : ''}`}
                  onClick={() => { void toggleSupport() }}
                  disabled={supportOn === null}
                >
                  <span />
                </button>
              </div>
            </Section>

            <Section icon={<TuneOutlined sx={{ fontSize: 18 }} />} title="Panel" sub="Kept in this browser">
              <div className={s.prefRow}>
                <span>Theme</span>
                <div className={s.seg}>
                  <button type="button" className={theme === 'light' ? s.segOn : ''} onClick={() => { if (theme !== 'light') toggleTheme() }}><LightModeIcon sx={{ fontSize: 15 }} />Light</button>
                  <button type="button" className={theme === 'dark' ? s.segOn : ''} onClick={() => { if (theme !== 'dark') toggleTheme() }}><DarkModeIcon sx={{ fontSize: 15 }} />Dark</button>
                </div>
              </div>
              <div className={s.prefRow}>
                <span>Pages</span>
                <div className={s.seg}>
                  <button type="button" className={view === 'classic' ? s.segOn : ''} onClick={() => setView('classic')}>Classic</button>
                  <button type="button" className={view === 'modern' ? s.segOn : ''} onClick={() => setView('modern')}>New</button>
                </div>
              </div>
            </Section>
          </div>

          <Section icon={<WarningAmberRounded sx={{ fontSize: 18 }} />} title="Delete account" sub="Removes your admin account — this cannot be undone" danger>
            <div className={s.dangerRow}>
              <span>{user.is2FAEnabled ? 'You will be asked for a two-step verification code.' : 'You will be asked to confirm.'}</span>
              <button type="button" className={s.dangerBtn} onClick={remove}>Delete my account</button>
            </div>
          </Section>
        </div>
      </div>

      {uploadOpen && <PopupImageUpload src={user.avatar} onClose={() => setUploadOpen(false)} />}
      {emailOpen && <ChangeEmailV2 onClose={() => setEmailOpen(false)} />}
      {passwordOpen && <ChangePasswordV2 onClose={() => setPasswordOpen(false)} />}
      {phoneOpen && <ChangePhoneV2 onClose={() => setPhoneOpen(false)} />}
      {/* 2-step verification: on (QR code, then a code), off, or the code for deleting the account */}
      {qrOpen && <TwoFactorV2 mode="enable" onClose={() => setQrOpen(false)} />}
      {codeFor && <TwoFactorV2 mode={codeFor === 'deleteUser' ? 'deleteUser' : 'disable'} onClose={() => setCodeFor(null)} />}
      {sureDelete && <ConfirmV2 header="Delete your account? This cannot be undone." onYes={() => dispatch(deleteUser(navigate))} onClose={() => setSureDelete(false)} />}
    </PrivatePage>
  )
}
